import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Heart, Shield, Users } from 'lucide-react';
import logo from '../logo2.png';

const AboutPage = () => {
    const navigate = useNavigate();
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-indigo-50">
            {/* Simple Navigation */}
            <nav className={`fixed top-0 w-full px-6 py-6 z-50 transition-all duration-300 ${scrollY > 20 ? 'bg-white/80 backdrop-blur-md border-b border-slate-100 shadow-sm' : 'bg-transparent'}`}>
                <div className="max-w-6xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/welcome')}>
                        <img src={logo} alt="CUConnect" className="w-8 h-8 object-contain" />
                        <span className="font-bold text-lg tracking-tight">CUConnect</span>
                    </div>
                    <button 
                        onClick={() => navigate('/welcome')}
                        className="text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-2"
                    >
                        <ArrowLeft size={16} />
                        Back to Home
                    </button>
                </div>
            </nav>

            <main className="pt-32 pb-24 px-6">
                <article className="max-w-3xl mx-auto">
                    {/* Header */}
                    <header className="mb-16">
                        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                            About <span className="text-slate-400 font-medium">CUConnect</span>
                        </h1>
                        <p className="text-xl text-slate-500 leading-relaxed font-medium">
                            The story behind the platform dedicated to the COMSATS student community.
                        </p>
                    </header>

                    {/* Content Sections */}
                    <div className="space-y-12 text-slate-600 leading-loose text-lg font-medium">
                        <section>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                                <BookOpen className="text-indigo-600" size={24} />
                                The Concept
                            </h2>
                            <p className="mb-6">
                                CUConnect was born out of a simple observation: students needed a unified, secure, and modern space to connect beyond the limitations of traditional social media and scattered messaging groups. We wanted to build something that feels official yet remains entirely community-driven—a "digital twin" of our campus life.
                            </p>
                            <p>
                                The platform isn't just a technical project; it's an effort to bring all COMSATS campuses together under one digital roof, starting with a focus on privacy and utility.
                            </p>
                        </section>

                        <section className="p-8 bg-slate-50 rounded-3xl border border-slate-100">
                            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                                <Shield className="text-indigo-600" size={24} />
                                Privacy First
                            </h2>
                            <p className="mb-4">
                                One of the core pillars of CUConnect is anonymity. We believe that students should be able to ask questions, share news, and express their thoughts without the pressure of a public profile. 
                            </p>
                            <p>
                                By using verified university emails, we ensure that the community remains exclusive to COMSATS students, while our "Ghost Identities" system allows you to interact freely under a secure alias. Your personal identity remains yours.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                                <Users className="text-indigo-600" size={24} />
                                Community Driven
                            </h2>
                            <p className="mb-6">
                                Everything in CUConnect is built with the student experience in mind. From the real-time campus pulse feed to our specialized GPA calculators, the goal is to provide tools that are actually useful in your day-to-day academic life.
                            </p>
                            <p>
                                We're not an official university platform, but we strive to maintain the same level of integrity and rigor. This is a project for students, by students, and it evolves based on the collective needs of our community.
                            </p>
                        </section>

                        <section className="flex items-center gap-6 p-8 border-2 border-slate-50 rounded-3xl">
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex gap-1 items-center justify-center text-red-500 shadow-sm shrink-0">
                                <Heart size={32} fill="currentColor" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-slate-900 mb-2">Built with Integrity</h3>
                                <p className="text-base text-slate-500 font-medium">
                                    Our mission is to foster a healthy, supportive, and connected academic environment. We welcome you to be a part of this journey.
                                </p>
                            </div>
                        </section>
                    </div>

                    {/* Final CTA */}
                    <div className="mt-20 pt-12 border-t border-slate-100 text-center">
                        <button 
                            onClick={() => navigate('/login')}
                            className="px-10 py-5 bg-slate-900 text-white rounded-2xl font-bold hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/10"
                        >
                            Get Started Today
                        </button>
                    </div>
                </article>
            </main>

            {/* Simple Footer */}
            <footer className="py-12 border-t border-slate-100 px-6">
                <div className="max-w-6xl mx-auto flex flex-col items-center md:flex-row md:justify-between gap-8 md:gap-6 text-slate-400">
                    <div className="flex flex-col items-center md:items-start gap-4">
                        <div className="flex items-center gap-3">
                            <img src={logo} alt="Logo" className="w-6 h-6 grayscale opacity-50" />
                            <span className="text-sm font-bold tracking-tight text-slate-400">CUConnect</span>
                        </div>
                        <p className="text-xs font-medium md:hidden">© {new Date().getFullYear()} CUConnect</p>
                    </div>
                    <div className="flex items-center gap-8 text-sm font-medium">
                        <button onClick={() => navigate('/welcome')} className="hover:text-slate-900 transition-colors">Home</button>
                        <button onClick={() => navigate('/login')} className="hover:text-slate-900 transition-colors">Login</button>
                    </div>
                    <p className="hidden md:block text-sm font-medium">© {new Date().getFullYear()} CUConnect</p>
                </div>
            </footer>
        </div>
    );
};

export default AboutPage;
